<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { formatAbsolute, formatRelative } from '@/lib/datetime';

const props = withDefaults(
    defineProps<{
        iso: string | null | undefined;
        // 'compact' (default): show relative only; absolute lives in the
        // title tooltip. 'verbose': show absolute prominently with the
        // relative time in parentheses next to it. Use verbose where the
        // exact timestamp is useful at a glance (history lists, audit views).
        verbose?: boolean;
    }>(),
    { verbose: false },
);

// The relative label drifts as time passes; re-render every 60s while
// mounted so "5 minutes ago" doesn't get stuck. We never show sub-minute
// granularity so 60s is plenty.
const tick = ref(0);
let timer: ReturnType<typeof setInterval> | undefined;
onMounted(() => { timer = setInterval(() => { tick.value += 1; }, 60_000); });
onBeforeUnmount(() => { if (timer) clearInterval(timer); });

const hasValue = computed(() => !!props.iso);
const relative = computed(() => {
    void tick.value;
    return props.iso ? formatRelative(props.iso) : '—';
});
const absolute = computed(() => (props.iso ? formatAbsolute(props.iso) : ''));
</script>

<template>
    <time
        v-if="hasValue && props.verbose"
        :datetime="props.iso ?? undefined"
        class="tabular-nums"
    >
        {{ absolute }}
        <span class="text-slate-400">({{ relative }})</span>
    </time>
    <time
        v-else-if="hasValue"
        :datetime="props.iso ?? undefined"
        :title="absolute"
        class="cursor-help underline decoration-dotted decoration-slate-500 underline-offset-2"
    >{{ relative }}</time>
    <span v-else class="text-slate-500">—</span>
</template>
